import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Shield, User, Car, RefreshCw, FileCheck, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Validation schemas for each step
const contactSchema = z.object({
  first_name: z.string().min(2, "First name is required"),
  last_name: z.string().min(2, "Last name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(10, "Valid phone number is required"),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip_code: z.string().optional(),
});

const preferencesSchema = z.object({
  vehicle_type: z.string().min(1, "Please select a vehicle type"),
  make_preference: z.string().optional(),
  model_preference: z.string().optional(),
  year_min: z.string().optional(),
  year_max: z.string().optional(),
  budget_min: z.string().optional(),
  budget_max: z.string().optional(),
  color_preference: z.string().optional(),
});

const tradeInSchema = z.object({
  has_trade_in: z.boolean(),
  trade_year: z.string().optional(),
  trade_make: z.string().optional(),
  trade_model: z.string().optional(),
  trade_mileage: z.string().optional(),
  trade_condition: z.string().optional(),
  trade_payoff: z.string().optional(),
});

const STEPS = [
  { id: 1, title: "Contact Info", icon: User },
  { id: 2, title: "Credit App", icon: CreditCard },
  { id: 3, title: "Preferences", icon: Car },
  { id: 4, title: "Trade-In", icon: RefreshCw },
  { id: 5, title: "Review", icon: FileCheck },
];

const VEHICLE_TYPES = [
  { id: "sedan", label: "Sedan", image: "https://images.unsplash.com/photo-1592150390752-9a223f2181fc?auto=format&fit=crop&w=300&q=60" },
  { id: "suv", label: "SUV", image: "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=300&q=60" },
  { id: "truck", label: "Truck", image: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&w=300&q=60" },
  { id: "other", label: "Other", image: null },
];

const CONDITIONS = ["Excellent", "Good", "Fair", "Poor"];

export const IntakeForm = ({ onBack }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applyForCredit, setApplyForCredit] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    contact: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
    },
    preferences: {
      vehicle_type: "",
      make_preference: "",
      model_preference: "",
      year_min: "",
      year_max: "",
      budget_min: "",
      budget_max: "",
      color_preference: "",
      features: [],
    },
    trade_in: {
      has_trade_in: false,
      trade_year: "",
      trade_make: "",
      trade_model: "",
      trade_mileage: "",
      trade_condition: "",
      trade_payoff: "",
    },
    credit_app: {
      applied_for_credit: false,
      middle_initial: "",
      date_of_birth: "",
      social_security_number: "",
      home_phone: "",
      work_phone: "",
      residence_address: "",
      residence_city: "",
      residence_state: "",
      residence_zip: "",
      apartment: "",
      housing_status: "",
      monthly_payment: "",
      time_at_address: "",
      employer_name: "",
      job_title: "",
      employment_status: "",
      monthly_income: "",
      time_at_job: "",
      authorized_credit_check: false,
    },
    notes: "",
  });

  // Contact form
  const contactForm = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: formData.contact,
  });

  // Preferences form
  const preferencesForm = useForm({
    resolver: zodResolver(preferencesSchema),
    defaultValues: formData.preferences,
  });

  // Trade-in form
  const tradeInForm = useForm({
    resolver: zodResolver(tradeInSchema),
    defaultValues: formData.trade_in,
  });

  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], ...data },
    }));
  };

  const handleNext = async () => {
    let isValid = false;
    
    if (currentStep === 1) {
      isValid = await contactForm.trigger();
      if (isValid) {
        updateFormData("contact", contactForm.getValues());
      }
    } else if (currentStep === 2) {
      // Credit App step - always valid (optional)
      isValid = true;
    } else if (currentStep === 3) {
      isValid = await preferencesForm.trigger();
      if (isValid) {
        updateFormData("preferences", preferencesForm.getValues());
      }
    } else if (currentStep === 4) {
      isValid = await tradeInForm.trigger();
      if (isValid) {
        updateFormData("trade_in", tradeInForm.getValues());
      }
    }

    if (isValid) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      onBack();
    } else {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const payload = {
        contact: formData.contact,
        preferences: {
          ...formData.preferences,
          year_min: formData.preferences.year_min ? parseInt(formData.preferences.year_min) : null,
          year_max: formData.preferences.year_max ? parseInt(formData.preferences.year_max) : null,
          budget_min: formData.preferences.budget_min ? parseInt(formData.preferences.budget_min) : null,
          budget_max: formData.preferences.budget_max ? parseInt(formData.preferences.budget_max) : null,
        },
        trade_in: {
          ...formData.trade_in,
          trade_year: formData.trade_in.trade_year ? parseInt(formData.trade_in.trade_year) : null,
          trade_mileage: formData.trade_in.trade_mileage ? parseInt(formData.trade_in.trade_mileage) : null,
          trade_payoff: formData.trade_in.trade_payoff ? parseFloat(formData.trade_in.trade_payoff) : null,
        },
        credit_app: applyForCredit ? {
          ...formData.credit_app,
          applied_for_credit: true,
          monthly_payment: formData.credit_app.monthly_payment ? parseFloat(formData.credit_app.monthly_payment) : null,
          monthly_income: formData.credit_app.monthly_income ? parseFloat(formData.credit_app.monthly_income) : null,
        } : { applied_for_credit: false },
        notes: formData.notes,
      };

      await axios.post(`${API}/leads`, payload);
      toast.success("Your information has been submitted successfully!");
      navigate("/success");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-8 max-w-4xl">
      {/* Logo at top of form */}
      <div className="flex justify-center mb-8">
        <img 
          src="https://customer-assets.emergentagent.com/job_intake-hub-6/artifacts/l9bs5fno_Cletis%20Logo.png"
          alt="Red River Internet Auto Sales"
          className="h-20 w-auto object-contain"
          data-testid="form-logo"
        />
      </div>

      {/* Progress Stepper */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                    currentStep > step.id
                      ? "bg-[#A52A2A] text-white"
                      : currentStep === step.id
                      ? "bg-[#A52A2A] text-white ring-4 ring-red-100"
                      : "bg-slate-100 text-slate-400"
                  }`}
                  data-testid={`step-indicator-${step.id}`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`hidden md:block text-xs mt-2 font-semibold ${
                  currentStep >= step.id ? "text-slate-900" : "text-slate-400"
                }`}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`w-12 md:w-24 h-1 mx-2 rounded ${
                  currentStep > step.id ? "bg-[#A52A2A]" : "bg-slate-100"
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        <div className="p-6 md:p-10">
          {/* Step 1: Contact Info */}
          {currentStep === 1 && (
            <div className="animate-fadeIn" data-testid="step-contact">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Contact Information
              </h2>
              <p className="text-slate-500 mb-8">Let us know how to reach you</p>

              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="first_name" className="text-sm font-semibold text-slate-700 mb-2 block">
                      First Name *
                    </Label>
                    <Input
                      id="first_name"
                      {...contactForm.register("first_name")}
                      className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                      placeholder="John"
                      data-testid="input-first-name"
                    />
                    {contactForm.formState.errors.first_name && (
                      <p className="text-red-500 text-sm mt-1">{contactForm.formState.errors.first_name.message}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Last Name *
                    </Label>
                    <Input
                      id="last_name"
                      {...contactForm.register("last_name")}
                      className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                      placeholder="Doe"
                      data-testid="input-last-name"
                    />
                    {contactForm.formState.errors.last_name && (
                      <p className="text-red-500 text-sm mt-1">{contactForm.formState.errors.last_name.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      {...contactForm.register("email")}
                      className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                    {contactForm.formState.errors.email && (
                      <p className="text-red-500 text-sm mt-1">{contactForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      {...contactForm.register("phone")}
                      className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                      placeholder="(555) 123-4567"
                      data-testid="input-phone"
                    />
                    {contactForm.formState.errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{contactForm.formState.errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="address" className="text-sm font-semibold text-slate-700 mb-2 block">
                    Street Address
                  </Label>
                  <Input
                    id="address"
                    {...contactForm.register("address")}
                    className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                    placeholder="123 Main Street"
                    data-testid="input-address"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="city" className="text-sm font-semibold text-slate-700 mb-2 block">
                      City
                    </Label>
                    <Input
                      id="city"
                      {...contactForm.register("city")}
                      className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                      placeholder="Houston"
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state" className="text-sm font-semibold text-slate-700 mb-2 block">
                      State
                    </Label>
                    <Input
                      id="state"
                      {...contactForm.register("state")}
                      className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                      placeholder="TX"
                      data-testid="input-state"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip_code" className="text-sm font-semibold text-slate-700 mb-2 block">
                      ZIP Code
                    </Label>
                    <Input
                      id="zip_code"
                      {...contactForm.register("zip_code")}
                      className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                      placeholder="77001"
                      data-testid="input-zip"
                    />
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Step 2: Credit Application (Optional) */}
          {currentStep === 2 && (
            <div className="animate-fadeIn" data-testid="step-credit-app">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Credit Application
              </h2>
              <p className="text-slate-500 mb-6">Optional - Get pre-approved for financing</p>

              {/* Toggle to apply for credit */}
              <div className="mb-8">
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <Checkbox
                    id="apply_for_credit"
                    checked={applyForCredit}
                    onCheckedChange={(checked) => setApplyForCredit(checked)}
                    className="w-5 h-5 border-2 data-[state=checked]:bg-[#A52A2A] data-[state=checked]:border-[#A52A2A]"
                    data-testid="checkbox-apply-credit"
                  />
                  <Label htmlFor="apply_for_credit" className="text-base font-medium text-slate-900 cursor-pointer">
                    Yes, I want to apply for credit/financing
                  </Label>
                </div>
              </div>

              {!applyForCredit ? (
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#A52A2A] rounded-full flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-2">Why Apply for Credit?</h3>
                      <ul className="text-slate-600 text-sm space-y-2">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          Know your budget before shopping
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          Faster vehicle purchase process
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          Access to competitive financing rates
                        </li>
                      </ul>
                      <p className="mt-4 text-sm text-slate-500">
                        Check the box above to fill out the credit application, or click Continue to skip this step.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Personal Info Section */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-[#A52A2A]" />
                      Personal Information
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">First Name</Label>
                        <Input
                          value={formData.contact.first_name}
                          disabled
                          className="h-10 bg-white border-slate-200"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Middle Initial</Label>
                        <Input
                          value={formData.credit_app.middle_initial}
                          onChange={(e) => updateFormData("credit_app", { middle_initial: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="M"
                          maxLength={1}
                          data-testid="input-middle-initial"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Last Name</Label>
                        <Input
                          value={formData.contact.last_name}
                          disabled
                          className="h-10 bg-white border-slate-200"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Date of Birth *</Label>
                        <Input
                          type="date"
                          value={formData.credit_app.date_of_birth}
                          onChange={(e) => updateFormData("credit_app", { date_of_birth: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          data-testid="input-dob"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Social Security Number *</Label>
                        <Input
                          value={formData.credit_app.social_security_number}
                          onChange={(e) => {
                            // Format as XXX-XX-XXXX
                            let value = e.target.value.replace(/\D/g, '').slice(0, 9);
                            if (value.length > 5) {
                              value = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5);
                            } else if (value.length > 3) {
                              value = value.slice(0, 3) + '-' + value.slice(3);
                            }
                            updateFormData("credit_app", { social_security_number: value });
                          }}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="XXX-XX-XXXX"
                          maxLength={11}
                          data-testid="input-ssn"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Home Phone *</Label>
                        <Input
                          value={formData.credit_app.home_phone}
                          onChange={(e) => updateFormData("credit_app", { home_phone: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="(555) 123-4567"
                          data-testid="input-home-phone"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Work Phone</Label>
                        <Input
                          value={formData.credit_app.work_phone}
                          onChange={(e) => updateFormData("credit_app", { work_phone: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="(555) 123-4567"
                          data-testid="input-work-phone"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Housing Info Section */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <h3 className="font-bold text-slate-900 mb-4">Housing Information</h3>
                    
                    <div className="grid grid-cols-1 gap-4 mb-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Street Address *</Label>
                        <Input
                          value={formData.credit_app.residence_address}
                          onChange={(e) => updateFormData("credit_app", { residence_address: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="123 Main Street"
                          data-testid="input-residence-address"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="col-span-2">
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">City *</Label>
                        <Input
                          value={formData.credit_app.residence_city}
                          onChange={(e) => updateFormData("credit_app", { residence_city: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="Houston"
                          data-testid="input-residence-city"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">State *</Label>
                        <Input
                          value={formData.credit_app.residence_state}
                          onChange={(e) => updateFormData("credit_app", { residence_state: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="TX"
                          data-testid="input-residence-state"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">ZIP Code *</Label>
                        <Input
                          value={formData.credit_app.residence_zip}
                          onChange={(e) => updateFormData("credit_app", { residence_zip: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="77001"
                          data-testid="input-residence-zip"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Apartment/Unit #</Label>
                        <Input
                          value={formData.credit_app.apartment}
                          onChange={(e) => updateFormData("credit_app", { apartment: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="Apt 101"
                          data-testid="input-apartment"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Housing Status *</Label>
                        <Select
                          value={formData.credit_app.housing_status}
                          onValueChange={(value) => updateFormData("credit_app", { housing_status: value })}
                        >
                          <SelectTrigger className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]" data-testid="select-housing-status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="own">Own</SelectItem>
                            <SelectItem value="rent">Rent</SelectItem>
                            <SelectItem value="live_with_family">Live with Family</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Monthly Rent/Mortgage</Label>
                        <Input
                          type="number"
                          value={formData.credit_app.monthly_payment}
                          onChange={(e) => updateFormData("credit_app", { monthly_payment: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="$1,200"
                          data-testid="input-monthly-payment"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Time at Address</Label>
                        <Select
                          value={formData.credit_app.time_at_address}
                          onValueChange={(value) => updateFormData("credit_app", { time_at_address: value })}
                        >
                          <SelectTrigger className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]" data-testid="select-time-at-address">
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="less_than_1_year">Less than 1 year</SelectItem>
                            <SelectItem value="1_2_years">1-2 years</SelectItem>
                            <SelectItem value="2_5_years">2-5 years</SelectItem>
                            <SelectItem value="5_plus_years">5+ years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Employment Info Section */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <h3 className="font-bold text-slate-900 mb-4">Employment Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Employment Status *</Label>
                        <Select
                          value={formData.credit_app.employment_status}
                          onValueChange={(value) => updateFormData("credit_app", { employment_status: value })}
                        >
                          <SelectTrigger className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]" data-testid="select-employment-status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="employed">Employed</SelectItem>
                            <SelectItem value="self_employed">Self-Employed</SelectItem>
                            <SelectItem value="retired">Retired</SelectItem>
                            <SelectItem value="student">Student</SelectItem>
                            <SelectItem value="unemployed">Unemployed</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Employer Name</Label>
                        <Input
                          value={formData.credit_app.employer_name}
                          onChange={(e) => updateFormData("credit_app", { employer_name: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="Company Name"
                          data-testid="input-employer"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Job Title</Label>
                        <Input
                          value={formData.credit_app.job_title}
                          onChange={(e) => updateFormData("credit_app", { job_title: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="Your Position"
                          data-testid="input-job-title"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Monthly Income *</Label>
                        <Input
                          type="number"
                          value={formData.credit_app.monthly_income}
                          onChange={(e) => updateFormData("credit_app", { monthly_income: e.target.value })}
                          className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="$4,000"
                          data-testid="input-monthly-income"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Time at Job</Label>
                        <Select
                          value={formData.credit_app.time_at_job}
                          onValueChange={(value) => updateFormData("credit_app", { time_at_job: value })}
                        >
                          <SelectTrigger className="h-10 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]" data-testid="select-time-at-job">
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="less_than_1_year">Less than 1 year</SelectItem>
                            <SelectItem value="1_2_years">1-2 years</SelectItem>
                            <SelectItem value="2_5_years">2-5 years</SelectItem>
                            <SelectItem value="5_plus_years">5+ years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Authorization */}
                  <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="authorize_credit_check"
                        checked={formData.credit_app.authorized_credit_check}
                        onCheckedChange={(checked) => updateFormData("credit_app", { authorized_credit_check: checked })}
                        className="w-5 h-5 mt-0.5 border-2 data-[state=checked]:bg-[#A52A2A] data-[state=checked]:border-[#A52A2A]"
                        data-testid="checkbox-authorize"
                      />
                      <div>
                        <Label htmlFor="authorize_credit_check" className="text-sm font-medium text-slate-900 cursor-pointer">
                          I authorize Red River Internet Auto Sales to obtain my credit report
                        </Label>
                        <p className="text-xs text-slate-500 mt-1">
                          By checking this box, you consent to allow us to pull your credit report for the purpose of evaluating your financing application.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Trust Badge */}
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                    <Shield className="w-4 h-4 text-green-500" />
                    <span>Your information is secure and encrypted</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Vehicle Preferences */}
          {currentStep === 3 && (
            <div className="animate-fadeIn" data-testid="step-preferences">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Vehicle Preferences
              </h2>
              <p className="text-slate-500 mb-8">Tell us what you're looking for</p>

              <form className="space-y-8">
                {/* Vehicle Type Selection */}
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-4 block">
                    What type of vehicle are you interested in? *
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {VEHICLE_TYPES.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => preferencesForm.setValue("vehicle_type", type.id)}
                        className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                          preferencesForm.watch("vehicle_type") === type.id
                            ? "border-[#A52A2A] bg-red-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`vehicle-type-${type.id}`}
                      >
                        {type.image ? (
                          <img
                            src={type.image}
                            alt={type.label}
                            className="w-full h-20 object-cover rounded-lg mb-2"
                          />
                        ) : (
                          <div className="w-full h-20 bg-slate-100 rounded-lg mb-2 flex items-center justify-center">
                            <Car className="w-8 h-8 text-slate-400" />
                          </div>
                        )}
                        <p className="font-semibold text-slate-900">{type.label}</p>
                        {preferencesForm.watch("vehicle_type") === type.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-[#A52A2A] rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  {preferencesForm.formState.errors.vehicle_type && (
                    <p className="text-red-500 text-sm mt-2">{preferencesForm.formState.errors.vehicle_type.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="make_preference" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Preferred Make
                    </Label>
                    <Input
                      id="make_preference"
                      {...preferencesForm.register("make_preference")}
                      className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                      placeholder="e.g., Toyota, Honda, Ford"
                      data-testid="input-make"
                    />
                  </div>
                  <div>
                    <Label htmlFor="model_preference" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Preferred Model
                    </Label>
                    <Input
                      id="model_preference"
                      {...preferencesForm.register("model_preference")}
                      className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                      placeholder="e.g., Camry, Civic, F-150"
                      data-testid="input-model"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                      Year Range
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        {...preferencesForm.register("year_min")}
                        className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                        placeholder="Min Year"
                        type="number"
                        data-testid="input-year-min"
                      />
                      <Input
                        {...preferencesForm.register("year_max")}
                        className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                        placeholder="Max Year"
                        type="number"
                        data-testid="input-year-max"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                      Budget Range
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        {...preferencesForm.register("budget_min")}
                        className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                        placeholder="Min $"
                        type="number"
                        data-testid="input-budget-min"
                      />
                      <Input
                        {...preferencesForm.register("budget_max")}
                        className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                        placeholder="Max $"
                        type="number"
                        data-testid="input-budget-max"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="color_preference" className="text-sm font-semibold text-slate-700 mb-2 block">
                    Preferred Color
                  </Label>
                  <Input
                    id="color_preference"
                    {...preferencesForm.register("color_preference")}
                    className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                    placeholder="e.g., Black, White, Silver"
                    data-testid="input-color"
                  />
                </div>
              </form>
            </div>
          )}

          {/* Step 4: Trade-In */}
          {currentStep === 4 && (
            <div className="animate-fadeIn" data-testid="step-tradein">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Trade-In Information
              </h2>
              <p className="text-slate-500 mb-8">Do you have a vehicle to trade in?</p>

              <form className="space-y-6">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="has_trade_in"
                    checked={tradeInForm.watch("has_trade_in")}
                    onCheckedChange={(checked) => tradeInForm.setValue("has_trade_in", checked)}
                    className="w-5 h-5 border-2 data-[state=checked]:bg-[#A52A2A] data-[state=checked]:border-[#A52A2A]"
                    data-testid="checkbox-tradein"
                  />
                  <Label htmlFor="has_trade_in" className="text-base font-medium text-slate-900 cursor-pointer">
                    Yes, I have a vehicle to trade in
                  </Label>
                </div>

                {tradeInForm.watch("has_trade_in") && (
                  <div className="space-y-6 pt-4 animate-fadeIn">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <Label htmlFor="trade_year" className="text-sm font-semibold text-slate-700 mb-2 block">
                          Year
                        </Label>
                        <Input
                          id="trade_year"
                          {...tradeInForm.register("trade_year")}
                          className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="2020"
                          type="number"
                          data-testid="input-trade-year"
                        />
                      </div>
                      <div>
                        <Label htmlFor="trade_make" className="text-sm font-semibold text-slate-700 mb-2 block">
                          Make
                        </Label>
                        <Input
                          id="trade_make"
                          {...tradeInForm.register("trade_make")}
                          className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="Toyota"
                          data-testid="input-trade-make"
                        />
                      </div>
                      <div>
                        <Label htmlFor="trade_model" className="text-sm font-semibold text-slate-700 mb-2 block">
                          Model
                        </Label>
                        <Input
                          id="trade_model"
                          {...tradeInForm.register("trade_model")}
                          className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="Camry"
                          data-testid="input-trade-model"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="trade_mileage" className="text-sm font-semibold text-slate-700 mb-2 block">
                          Current Mileage
                        </Label>
                        <Input
                          id="trade_mileage"
                          {...tradeInForm.register("trade_mileage")}
                          className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                          placeholder="50000"
                          type="number"
                          data-testid="input-trade-mileage"
                        />
                      </div>
                      <div>
                        <Label htmlFor="trade_condition" className="text-sm font-semibold text-slate-700 mb-2 block">
                          Condition
                        </Label>
                        <Select
                          value={tradeInForm.watch("trade_condition")}
                          onValueChange={(value) => tradeInForm.setValue("trade_condition", value)}
                        >
                          <SelectTrigger className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]" data-testid="select-trade-condition">
                            <SelectValue placeholder="Select condition" />
                          </SelectTrigger>
                          <SelectContent>
                            {CONDITIONS.map((condition) => (
                              <SelectItem key={condition} value={condition.toLowerCase()}>
                                {condition}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="trade_payoff" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Remaining Loan Balance (if any)
                      </Label>
                      <Input
                        id="trade_payoff"
                        {...tradeInForm.register("trade_payoff")}
                        className="h-12 border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                        placeholder="$0"
                        type="number"
                        data-testid="input-trade-payoff"
                      />
                    </div>
                  </div>
                )}
              </form>
            </div>
          )}

          {/* Step 5: Review */}
          {currentStep === 5 && (
            <div className="animate-fadeIn" data-testid="step-review">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Review Your Information
              </h2>
              <p className="text-slate-500 mb-8">Please verify your details before submitting</p>

              <div className="space-y-6">
                {/* Contact Summary */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-[#A52A2A]" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Name:</span>
                      <span className="ml-2 font-medium text-slate-900" data-testid="review-name">
                        {formData.contact.first_name} {formData.contact.last_name}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Email:</span>
                      <span className="ml-2 font-medium text-slate-900" data-testid="review-email">{formData.contact.email}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Phone:</span>
                      <span className="ml-2 font-medium text-slate-900" data-testid="review-phone">{formData.contact.phone}</span>
                    </div>
                    {formData.contact.city && (
                      <div>
                        <span className="text-slate-500">Location:</span>
                        <span className="ml-2 font-medium text-slate-900">
                          {formData.contact.city}, {formData.contact.state} {formData.contact.zip_code}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Preferences Summary */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Car className="w-5 h-5 text-[#A52A2A]" />
                    Vehicle Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Type:</span>
                      <span className="ml-2 font-medium text-slate-900 capitalize" data-testid="review-vehicle-type">
                        {formData.preferences.vehicle_type}
                      </span>
                    </div>
                    {formData.preferences.make_preference && (
                      <div>
                        <span className="text-slate-500">Make:</span>
                        <span className="ml-2 font-medium text-slate-900">{formData.preferences.make_preference}</span>
                      </div>
                    )}
                    {formData.preferences.model_preference && (
                      <div>
                        <span className="text-slate-500">Model:</span>
                        <span className="ml-2 font-medium text-slate-900">{formData.preferences.model_preference}</span>
                      </div>
                    )}
                    {(formData.preferences.year_min || formData.preferences.year_max) && (
                      <div>
                        <span className="text-slate-500">Year Range:</span>
                        <span className="ml-2 font-medium text-slate-900">
                          {formData.preferences.year_min || "Any"} - {formData.preferences.year_max || "Any"}
                        </span>
                      </div>
                    )}
                    {(formData.preferences.budget_min || formData.preferences.budget_max) && (
                      <div>
                        <span className="text-slate-500">Budget:</span>
                        <span className="ml-2 font-medium text-slate-900">
                          ${formData.preferences.budget_min || "0"} - ${formData.preferences.budget_max || "No limit"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Trade-In Summary */}
                {formData.trade_in.has_trade_in && (
                  <div className="bg-slate-50 rounded-xl p-6">
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-[#A52A2A]" />
                      Trade-In Vehicle
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Vehicle:</span>
                        <span className="ml-2 font-medium text-slate-900" data-testid="review-trade-vehicle">
                          {formData.trade_in.trade_year} {formData.trade_in.trade_make} {formData.trade_in.trade_model}
                        </span>
                      </div>
                      {formData.trade_in.trade_mileage && (
                        <div>
                          <span className="text-slate-500">Mileage:</span>
                          <span className="ml-2 font-medium text-slate-900">
                            {parseInt(formData.trade_in.trade_mileage).toLocaleString()} miles
                          </span>
                        </div>
                      )}
                      {formData.trade_in.trade_condition && (
                        <div>
                          <span className="text-slate-500">Condition:</span>
                          <span className="ml-2 font-medium text-slate-900 capitalize">{formData.trade_in.trade_condition}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Credit Application Summary */}
                {applyForCredit && (
                  <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-[#A52A2A]" />
                      Credit Application
                      <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full ml-2">Included</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Date of Birth:</span>
                        <span className="ml-2 font-medium text-slate-900">{formData.credit_app.date_of_birth || "Not provided"}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">SSN:</span>
                        <span className="ml-2 font-medium text-slate-900">
                          {formData.credit_app.social_security_number ? 
                            `***-**-${formData.credit_app.social_security_number.slice(-4)}` : 
                            "Not provided"}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Employment:</span>
                        <span className="ml-2 font-medium text-slate-900 capitalize">{formData.credit_app.employment_status?.replace(/_/g, ' ') || "Not specified"}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Monthly Income:</span>
                        <span className="ml-2 font-medium text-slate-900">${formData.credit_app.monthly_income || "0"}</span>
                      </div>
                      {formData.credit_app.employer_name && (
                        <div>
                          <span className="text-slate-500">Employer:</span>
                          <span className="ml-2 font-medium text-slate-900">{formData.credit_app.employer_name}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-slate-500">Credit Check:</span>
                        <span className={`ml-2 font-medium ${formData.credit_app.authorized_credit_check ? "text-green-600" : "text-amber-600"}`}>
                          {formData.credit_app.authorized_credit_check ? "Authorized" : "Not authorized"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Notes */}
                <div>
                  <Label htmlFor="notes" className="text-sm font-semibold text-slate-700 mb-2 block">
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="min-h-[100px] border-slate-200 focus:border-[#A52A2A] focus:ring-[#A52A2A]"
                    placeholder="Any specific requirements or questions?"
                    data-testid="input-notes"
                  />
                </div>

                {/* Trust Badge */}
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Your information is secure and will never be shared</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="px-6 md:px-10 py-6 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
          <Button
            type="button"
            variant="ghost"
            onClick={handleBack}
            className="gap-2 text-slate-600 hover:text-slate-900"
            data-testid="btn-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>

          {currentStep < 5 ? (
            <Button
              type="button"
              onClick={handleNext}
              className="gap-2 bg-[#A52A2A] hover:bg-[#8B2323] text-white px-8 py-3 font-bold uppercase tracking-wide shadow-[0_4px_14px_0_rgba(165,42,42,0.39)]"
              data-testid="btn-next"
            >
              {currentStep === 2 ? "Continue" : "Next"}
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="gap-2 bg-[#A52A2A] hover:bg-[#8B2323] text-white px-8 py-3 font-bold uppercase tracking-wide shadow-[0_4px_14px_0_rgba(165,42,42,0.39)]"
              data-testid="btn-submit"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
              <Check className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

    </div>
  );
};

export default IntakeForm;
